import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import Button from 'components/ds/Button'
import Badge from 'components/ds/Badge'
import { Modal } from 'components/ds/Feedback'
import { Input } from 'components/ds/Input'
import Icon from 'components/ds/Icon'
import { Toast } from 'components/shared/Toaster'
import request from 'tools/request'
import { unshareApplication } from 'actions/applicationShares'

const STATUS_TONES = {
    'New': 'review', 'Under Review': 'review', 'Pending': 'watch', 'Approved': 'approved',
    'Cautionary Approval': 'approved', 'Declined': 'declined', 'Cancelled': 'closed'
}
const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0', borderBottom: '1px solid var(--border-default)' }
const mutedStyle = { color: 'var(--fg-secondary)', fontSize: 13 }
// The list scrolls inside the dialog and loads a page at a time; search shows up once it is long enough to need it
const listStyle = { maxHeight: 360, overflowY: 'auto', paddingRight: 4 }
const PAGE_SIZE = 50
const SEARCH_FROM = 6

/** The applications shared with a sales representative's login, each with a link and a Remove action. */
const SharedApplicationsModal = ({ representative, onClose }) => {
    // { userId, total, applications } for everything loaded so far
    const [data, setData] = useState(null)
    // Applications shared at all, before any search: decides whether the search box shows
    const [sharedTotal, setSharedTotal] = useState(0)
    const [search, setSearch] = useState('')
    const [loadingMore, setLoadingMore] = useState(false)
    const searchTimer = useRef()
    // Only the latest request may update the list: a slower, older search must not overwrite a newer one
    const requestId = useRef(0)

    const fetchPage = useCallback((term, page) => request(
        `salesRepresentatives/${representative.uuid}/shared-applications`, 'GET', null, false, {}, { search: term, page, size: PAGE_SIZE }
    ), [representative])

    const load = useCallback((term = '') => {
        const id = ++requestId.current
        if (!representative) return
        setData(null)
        fetchPage(term, 0)
            .then(res => {
                if (id !== requestId.current) return
                const result = res || { applications: [], total: 0 }
                setData(result)
                if (!term) setSharedTotal(result.total || 0)
            })
            .catch(() => { if (id === requestId.current) setData({ applications: [], total: 0 }) })
    }, [representative, fetchPage])

    useEffect(() => {
        setSearch('')
        setSharedTotal(0)
        load()
    }, [load])

    // Closing or unmounting drops any request still on its way
    useEffect(() => () => {
        clearTimeout(searchTimer.current)
        requestId.current++
    }, [representative])

    const onSearch = (value) => {
        setSearch(value)
        clearTimeout(searchTimer.current)
        searchTimer.current = setTimeout(() => load(value.trim()), 300)
    }

    const loadMore = () => {
        const id = requestId.current
        setLoadingMore(true)
        fetchPage(search.trim(), Math.floor(data.applications.length / PAGE_SIZE))
            .then(res => {
                if (id !== requestId.current) return
                setData(current => ({ ...current, applications: [...current.applications, ...(res?.applications || [])] }))
            })
            .catch(() => {})
            .finally(() => setLoadingMore(false))
    }

    const remove = (application) => {
        unshareApplication(application.applicationId, data.userId)
            .then(() => {
                Toast({ type: 'success', message: `${application.businessName} is no longer shared` })
                load(search.trim())
            })
            .catch(() => {})
    }

    const name = representative ? `${representative.firstName} ${representative.lastName}` : ''
    const applications = data?.applications || []
    const total = data?.total || 0

    return (
        <Modal
            open={!!representative}
            onClose={onClose}
            title={sharedTotal ? `Shared with ${name} (${sharedTotal})` : `Shared with ${name}`}
            footer={<Button variant='secondary' size='small' onClick={onClose}>Close</Button>}
        >
            {sharedTotal >= SEARCH_FROM &&
                <div style={{ marginBottom: 8 }}>
                    <Input
                        placeholder='Search business name'
                        value={search}
                        onChange={e => onSearch(e.target.value)}
                        startIcon={<Icon name='search' size={16} />}
                    />
                </div>
            }
            {!data && <p style={mutedStyle}>Loading...</p>}
            {data && total === 0 && (search.trim()
                ? <p style={mutedStyle}>No shared application matches "{search.trim()}".</p>
                : <p style={mutedStyle}>No applications are shared with {name} yet. Share one from the application's page.</p>
            )}
            <div style={listStyle}>
                {applications.map(application =>
                    <div key={application.applicationId} style={rowStyle}>
                        <div>
                            <Link to={`/applicant/${application.applicationId}`} onClick={onClose}>{application.businessName}</Link>
                            <div style={mutedStyle}>
                                Shared {moment(application.sharedAt).format('MMM D, YYYY')}{application.sharedBy ? ` by ${application.sharedBy}` : ''}
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {application.status && <Badge tone={STATUS_TONES[application.status] || 'closed'}>{application.status}</Badge>}
                            <Button variant='ghost' size='small' onClick={() => remove(application)}>Remove</Button>
                        </div>
                    </div>
                )}
                {applications.length < total &&
                    <div style={{ padding: '12px 0', textAlign: 'center' }}>
                        <Button variant='secondary' size='small' loading={loadingMore} onClick={loadMore}>
                            Load more ({total - applications.length} left)
                        </Button>
                    </div>
                }
            </div>
        </Modal>
    )
}

export default SharedApplicationsModal
